import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import cls from "./UserMemberInfo.module.scss";
import api from "@/utils/axios";
import { toast } from "react-toastify";

import {
  EXPERIENCE_LEVELS,
  MEMBER_STATUSES,
  SECONDARY_LANGUAGES,
  MEMBER_TYPES,
  MEMBER_ORIGINS,
} from "@/enums/MemberEnums";

const UserMemberInfo = () => {
  const { userId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [member, setMember] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);

  const [experienceLevel, setExperienceLevel] = useState("");
  const [status, setStatus] = useState("");
  const [secondaryLanguage, setSecondaryLanguage] = useState("");
  const [memberType, setMemberType] = useState("");
  const [origin, setOrigin] = useState("");
  const [memberRenewDay, setMemberRenewDay] = useState("");

  // Friendly label for statuses etc.
  const formatLabel = (key) => {
    if (!key) return "";
    return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const languageLabel = (code) => {
    const map = {
      en: "English",
      si: "සිංහල (Sinhala)",
      ta: "தமிழ் (Tamil)",
      hi: "हिन्दी (Hindi)",
    };
    return map[code] || code.toUpperCase();
  };

  const typeLabel = (t) => {
    if (t === "single") return "Single";
    if (t === "couple") return "Couple";
    return formatLabel(t);
  };

  const originLabel = (o) => {
    if (o === "real") return "Real Member";
    if (o === "test") return "Test / Sandbox";
    if (o === "bot") return "Bot";
    return formatLabel(o);
  };

  const avatarUrl = useMemo(() => {
    if (avatarPreview) return avatarPreview;
    if (member?.avatar?.url) return member.avatar.url;
    return null;
  }, [avatarPreview, member]);

  const loadMember = async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const res = await api.get(
        `/v1/admin/members/single/member-info/${userId}`
      );
      const data = res.data?.member;
      setMember(data || null);

      if (data) {
        setExperienceLevel(data.experienceLevel || "");
        setStatus(data.status || "");
        setSecondaryLanguage(data.secondaryLanguage || "");
        setMemberType(data.memberType || "");
        setOrigin(data.origin || "");
        if (data.member_renew_day) {
          const d = new Date(data.member_renew_day);
          if (!Number.isNaN(d.getTime())) {
            setMemberRenewDay(d.toISOString().slice(0, 10));
          }
        } else {
          setMemberRenewDay("");
        }
      }
      setAvatarPreview(null);
      setAvatarFile(null);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load member info");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMember();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!member) return;

    try {
      setSaving(true);
      await api.patch(`/v1/admin/members/single/member-info/${userId}`, {
        experienceLevel: experienceLevel || undefined,
        status: status || undefined,
        secondaryLanguage: secondaryLanguage || undefined,
        memberType: memberType || undefined,
        origin: origin || undefined,
        member_renew_day: memberRenewDay || null,
      });

      toast.success("Member details updated");
      await loadMember();
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.message || "Failed to update member info";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setAvatarFile(null);
      setAvatarPreview(null);
      return;
    }

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    setAvatarFile(file);
    const url = URL.createObjectURL(file);
    setAvatarPreview(url);
  };

  const handleUploadAvatar = async () => {
    if (!avatarFile) {
      toast.error("Please choose an image first");
      return;
    }

    try {
      setUploading(true);

      const file = avatarFile;
      const ext =
        file.name && file.name.includes(".")
          ? file.name.split(".").pop().toLowerCase()
          : "webp";
      const contentType = file.type || "image/webp";

      // 1) Request presigned URL from backend
      const res = await api.post(
        `/v1/admin/members/single/avatar/presign/${userId}`,
        {
          ext,
          contentType,
        }
      );

      const { uploadUrl } = res.data || {};
      if (!uploadUrl) {
        toast.error("Presign failed: missing upload URL");
        return;
      }

      // 2) Upload directly to S3
      const putRes = await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": contentType,
        },
        body: file,
      });

      if (!putRes.ok) {
        console.error("S3 upload failed", putRes.status, await putRes.text());
        toast.error("Failed to upload image to S3");
        return;
      }

      toast.success("Avatar uploaded successfully");
      setAvatarFile(null);
      setAvatarPreview(null);

      // 3) Refresh member data to get new signed avatar URL
      await loadMember();
    } catch (err) {
      console.error(err);
      toast.error("Failed to upload avatar");
    } finally {
      setUploading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (loading && !member) {
    return (
      <div className={cls.page}>
        <div className={cls.header}>
          <button type="button" className={cls.backBtn} onClick={handleBack}>
            ← Back
          </button>
          <h1 className={cls.title}>Member Info</h1>
          <p className={cls.subtitle}>Loading member details…</p>
        </div>
        <div className={cls.skeleton} />
      </div>
    );
  }

  if (!member) {
    return (
      <div className={cls.page}>
        <div className={cls.header}>
          <button type="button" className={cls.backBtn} onClick={handleBack}>
            ← Back
          </button>
          <h1 className={cls.title}>Member Info</h1>
          <p className={cls.subtitle}>No member found for this user.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={cls.page}>
      <div className={cls.header}>
        <button type="button" className={cls.backBtn} onClick={handleBack}>
          ← Back
        </button>
        <h1 className={cls.title}>Member Core Details</h1>
        <p className={cls.subtitle}>
          Admin view to review and edit core member settings. Avatar, experience
          level, status, language, type, and origin.
        </p>
      </div>

      <div className={cls.metaBar}>
        <div className={cls.metaItem}>
          <span className={cls.metaLabel}>User ID</span>
          <span className={cls.metaValue}>{member.userId}</span>
        </div>
        <div className={cls.metaItem}>
          <span className={cls.metaLabel}>Member ID</span>
          <span className={cls.metaValue}>{member.id}</span>
        </div>
        <div className={cls.metaItem}>
          <span className={cls.metaLabel}>Created</span>
          <span className={cls.metaValue}>
            {member.createdAt
              ? new Date(member.createdAt).toLocaleString()
              : "—"}
          </span>
        </div>
        <div className={cls.metaItem}>
          <span className={cls.metaLabel}>Updated</span>
          <span className={cls.metaValue}>
            {member.updatedAt
              ? new Date(member.updatedAt).toLocaleString()
              : "—"}
          </span>
        </div>
      </div>

      <div className={cls.layout}>
        {/* LEFT COLUMN – AVATAR */}
        <section className={cls.avatarCard}>
          <div className={cls.avatarHeader}>
            <h2 className={cls.cardTitle}>Avatar</h2>
            <p className={cls.cardSubtitle}>
              Update the member&apos;s private avatar used across the club.
            </p>
          </div>

          <div className={cls.avatarPreviewWrap}>
            <div className={cls.avatarGlow}>
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt="Member avatar"
                  className={cls.avatarImage}
                />
              ) : (
                <div className={cls.avatarFallback}>No avatar</div>
              )}
            </div>
            {member.avatar?.updatedAt && (
              <p className={cls.avatarMeta}>
                Last updated{" "}
                {new Date(member.avatar.updatedAt).toLocaleString()}
              </p>
            )}
          </div>

          <div className={cls.avatarForm}>
            <label className={cls.fileLabel}>
              <span className={cls.fileLabelText}>Choose new avatar</span>
              <input
                type="file"
                accept="image/*"
                onChange={handleAvatarFileChange}
              />
            </label>

            {avatarFile && (
              <p className={cls.fileName}>
                Selected: <strong>{avatarFile.name}</strong>
              </p>
            )}

            <button
              type="button"
              className={cls.avatarBtn}
              onClick={handleUploadAvatar}
              disabled={uploading || !avatarFile}
            >
              {uploading ? "Uploading…" : "Upload Avatar"}
            </button>
          </div>
        </section>

        {/* RIGHT COLUMN – FORM */}
        <section className={cls.formCard}>
          <div className={cls.formHeader}>
            <h2 className={cls.cardTitle}>Core Member Settings</h2>
            <p className={cls.cardSubtitle}>
              Adjust experience level, status, language, type, origin, and
              renewal day.
            </p>
          </div>

          <form className={cls.form} onSubmit={handleSave}>
            {/* Row: Experience + Status */}
            <div className={cls.formRow}>
              <div className={cls.field}>
                <label className={cls.label} htmlFor="experienceLevel">
                  Experience level
                </label>
                <select
                  id="experienceLevel"
                  className={cls.select}
                  value={experienceLevel}
                  onChange={(e) => setExperienceLevel(e.target.value)}
                >
                  <option value="">Select experience</option>
                  {EXPERIENCE_LEVELS.map((lvl) => (
                    <option key={lvl} value={lvl}>
                      {formatLabel(lvl)}
                    </option>
                  ))}
                </select>
              </div>

              <div className={cls.field}>
                <label className={cls.label} htmlFor="status">
                  Member status
                </label>
                <select
                  id="status"
                  className={cls.select}
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="">Select status</option>
                  {MEMBER_STATUSES.map((st) => (
                    <option key={st} value={st}>
                      {formatLabel(st)}
                    </option>
                  ))}
                </select>
                {status && (
                  <span
                    className={`${cls.statusPill} ${cls[`status_${status}`]}`}
                  >
                    {formatLabel(status)}
                  </span>
                )}
              </div>
            </div>

            {/* Row: Language + Type */}
            <div className={cls.formRow}>
              <div className={cls.field}>
                <label className={cls.label} htmlFor="secondaryLanguage">
                  Secondary language
                </label>
                <select
                  id="secondaryLanguage"
                  className={cls.select}
                  value={secondaryLanguage}
                  onChange={(e) => setSecondaryLanguage(e.target.value)}
                >
                  <option value="">None / Default</option>
                  {SECONDARY_LANGUAGES.map((lng) => (
                    <option key={lng} value={lng}>
                      {languageLabel(lng)}
                    </option>
                  ))}
                </select>
              </div>

              <div className={cls.field}>
                <label className={cls.label} htmlFor="memberType">
                  Member type
                </label>
                <select
                  id="memberType"
                  className={cls.select}
                  value={memberType}
                  onChange={(e) => setMemberType(e.target.value)}
                >
                  <option value="">Select type</option>
                  {MEMBER_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {typeLabel(t)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Row: Origin + Renewal Day */}
            <div className={cls.formRow}>
              <div className={cls.field}>
                <label className={cls.label} htmlFor="origin">
                  Member origin
                </label>
                <select
                  id="origin"
                  className={cls.select}
                  value={origin}
                  onChange={(e) => setOrigin(e.target.value)}
                >
                  <option value="">Select origin</option>
                  {MEMBER_ORIGINS.map((o) => (
                    <option key={o} value={o}>
                      {originLabel(o)}
                    </option>
                  ))}
                </select>
              </div>

              <div className={cls.field}>
                <label className={cls.label} htmlFor="memberRenewDay">
                  Renewal day
                </label>
                <input
                  id="memberRenewDay"
                  type="date"
                  className={cls.input}
                  value={memberRenewDay}
                  onChange={(e) => setMemberRenewDay(e.target.value)}
                />
                <p className={cls.help}>
                  Optional. Used for membership renewal tracking.
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className={cls.actions}>
              <button
                type="button"
                className={cls.secondaryBtn}
                onClick={loadMember}
                disabled={saving}
              >
                Reset / Refresh
              </button>
              <button
                type="submit"
                className={cls.primaryBtn}
                disabled={saving}
              >
                {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
};

export default UserMemberInfo;
